/**
 * useNewImport — the New Import wizard's submit pipeline (sub-task 03).
 *
 * Drives the 3-step presigned-PUT handshake:
 *   1. POST /imports         → { import_id, s3_key, presigned_put_url }
 *   2. browser PUT to S3     (XHR — captures upload-progress events)
 *   3. POST /imports/{id}/complete → 202 { job_id, status:"parsing", poll_url }
 * then toasts "N contacts imported — processing…" and navigates to the Imports
 * list.
 *
 * Submit states: `idle → uploading → finalizing → processing` (+ `error`).
 *   - uploading  = the S3 PUT with the progress bar
 *   - finalizing = the POST /complete call
 *   - processing = handed off to the worker (terminal for the wizard; the list
 *                  page polls the parse status)
 *
 * PUT failure → `error` + a retry path: re-PUT the same URL; if the presign has
 * expired (403 / past TTL) we re-request POST /imports for a fresh URL first
 * (FR-03.9).
 *
 * A 422 from `/complete` → `rejected`. The server read the file's header and
 * declined it (no `First Name` / `Last Name` / `LinkedIn URL` column — a
 * searches export, say). The import was NEVER created: `/complete` is the
 * atomic create-on-complete step and it refused before inserting the row. So
 * this is not an upload failure and gets no Retry — retrying a file that will
 * never parse is noise — the reason is shown in place and the user picks
 * another file. Before this the same file became an import that existed and
 * was `failed`, with the reason one page away.
 *
 * The uploader is INJECTABLE (`putToS3`) so tests can drive progress + failures
 * deterministically; the default uses XHR for real upload-progress events.
 */

import { useCallback, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import type { ImportCreated } from "@nu-assist/contracts";
import {
  type CreateImportPayload,
  ImportsApiError,
  type ImportsClient,
  createImportsClient,
} from "../../../clients/imports.client";

const API_BASE =
  import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

export type SubmitState =
  | "idle"
  | "uploading"
  | "finalizing"
  | "processing"
  | "error"
  | "rejected";

/** The server declined the FILE (422 on `/complete`): no import exists for it.
 *  Distinct from every other pipeline failure so `onError` can route it to
 *  `rejected` (reason in place, no Retry) instead of `error` (Retry). */
export class ImportRefusedError extends Error {
  constructor(public readonly reason: string) {
    super(reason);
    this.name = "ImportRefusedError";
    Object.setPrototypeOf(this, ImportRefusedError.prototype);
  }
}

/** Shown for every failure that is NOT a refusal — the file may be fine. */
export const UPLOAD_FAILED_MESSAGE =
  "Upload failed. Check your connection and try again.";

/** Result of a presigned PUT: ok, or expired (re-request a fresh URL). */
export interface PutResult {
  ok: boolean;
  /** True when the URL was rejected for expiry (403) — re-request POST /imports. */
  expired?: boolean;
  status?: number;
}

export type PutToS3 = (args: {
  url: string;
  file: File;
  contentType: string;
  onProgress: (fraction: number) => void;
}) => Promise<PutResult>;

/** Default XHR uploader — real upload-progress events + a clean abort handle. */
export const xhrPutToS3: PutToS3 = ({ url, file, contentType, onProgress }) =>
  new Promise<PutResult>((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve({ ok: true, status: xhr.status });
      } else {
        // 403 from S3 commonly means the presigned URL's TTL lapsed.
        resolve({ ok: false, expired: xhr.status === 403, status: xhr.status });
      }
    };
    xhr.onerror = () => resolve({ ok: false, status: xhr.status });
    xhr.send(file);
  });

export interface NewImportInput {
  file: File;
  source: CreateImportPayload["source"];
  importFormat?: CreateImportPayload["import_format"];
  /** REQUIRED for the MVP — every import is scoped to a Thrive search. The
   *  page gates the submit CTA on a selection, so this is always set here. */
  searchIdThrive: string;
  /** Selected search's display name — persisted as `search_name_cached` so the
   *  list shows the linked search instead of "Unlinked". */
  searchNameThrive?: string | null;
  contactCount: number;
}

export interface UseNewImportOptions {
  /** Injected for tests; defaults to the XHR uploader. */
  putToS3?: PutToS3;
  /** Injected for tests; defaults to the real imports client. */
  apiBase?: string;
  /** Injected for tests; defaults to a client built from `apiBase`. */
  client?: ImportsClient;
}

export interface UseNewImportResult {
  submit: (input: NewImportInput) => void;
  retry: () => void;
  /** Back to `idle` (clears the error). The page calls it when a new file is
   *  chosen after a refusal or a failure, so a stale message does not sit
   *  under a fresh selection. */
  reset: () => void;
  state: SubmitState;
  progress: number;
  error: string | null;
  isBusy: boolean;
}

export function useNewImport(
  options: UseNewImportOptions = {},
): UseNewImportResult {
  const navigate = useNavigate();
  const putToS3 = options.putToS3 ?? xhrPutToS3;
  const client =
    options.client ??
    createImportsClient({ apiBase: options.apiBase ?? API_BASE });

  const [state, setState] = useState<SubmitState>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Remember the last input so `retry()` can re-run without re-collecting it.
  const lastInput = useRef<NewImportInput | null>(null);

  const runPipeline = useCallback(
    async (input: NewImportInput) => {
      setError(null);
      lastInput.current = input;

      // Step 1 — create + presign (or re-request a fresh URL on retry).
      const created: ImportCreated = await client.createImport({
        filename: input.file.name,
        content_type: input.file.type || "application/octet-stream",
        size_bytes: input.file.size,
        import_format: input.importFormat ?? "baby_chops_v1",
        source: input.source,
        search_id_thrive: input.searchIdThrive,
      });

      // Step 2 — presigned PUT (XHR progress).
      setState("uploading");
      setProgress(0);
      let put = await putToS3({
        url: created.presigned_put_url,
        file: input.file,
        contentType: input.file.type || "application/octet-stream",
        onProgress: setProgress,
      });

      // Expired URL → re-request POST /imports for a fresh URL, then re-PUT.
      if (!put.ok && put.expired) {
        const fresh = await client.createImport({
          filename: input.file.name,
          content_type: input.file.type || "application/octet-stream",
          size_bytes: input.file.size,
          import_format: input.importFormat ?? "baby_chops_v1",
          source: input.source,
          search_id_thrive: input.searchIdThrive,
        });
        put = await putToS3({
          url: fresh.presigned_put_url,
          file: input.file,
          contentType: input.file.type || "application/octet-stream",
          onProgress: setProgress,
        });
        created.import_id = fresh.import_id;
        created.s3_key = fresh.s3_key;
      }

      if (!put.ok) {
        throw new Error("upload-failed");
      }

      // Step 3 — finalize → enqueue parse. Atomic create-on-complete: the row
      // is inserted server-side HERE, so we echo back the s3_key + metadata
      // from step 1 (a failed PUT above leaves no orphan import row).
      setState("finalizing");
      try {
        await client.completeImport(created.import_id, {
          s3_key: created.s3_key,
          filename: input.file.name,
          import_format: input.importFormat ?? "baby_chops_v1",
          source: input.source ?? "csv_upload",
          search_id_thrive: input.searchIdThrive,
          search_name: input.searchNameThrive ?? null,
        });
      } catch (err) {
        // 422 WITH a string `detail` = the server read the header and
        // declined the file; the detail is the reason, written for the
        // uploader. A 422 without one is FastAPI's validation-error list —
        // client/server drift, not a verdict on the file — and stays on the
        // retryable path with the generic message, like a 409 or a 5xx.
        if (err instanceof ImportsApiError && err.status === 422 && typeof err.detail === "string") {
          throw new ImportRefusedError(err.detail);
        }
        throw err;
      }

      // Handed off to the worker.
      setState("processing");
      return input.contactCount;
    },
    [client, putToS3],
  );

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: runPipeline,
    onSuccess: (count) => {
      // Refresh the firm-wide owner set — the imports table just changed.
      // Note it cannot change what THIS user sees: the row it adds is their own
      // email, which `ImportOwnerMenu` filters out. The value is picking up
      // other recruiters' first-ever imports without waiting out `staleTime`.
      void queryClient.invalidateQueries({ queryKey: ["imports", "owners"] });
      toast.success(`${count} contacts imported — processing…`);
      navigate("/research/imports");
    },
    onError: (err) => {
      if (err instanceof ImportRefusedError) {
        setState("rejected");
        setError(err.reason);
        return;
      }
      setState("error");
      setError(UPLOAD_FAILED_MESSAGE);
    },
  });

  const submit = useCallback(
    (input: NewImportInput) => mutation.mutate(input),
    [mutation],
  );

  const retry = useCallback(() => {
    if (lastInput.current) mutation.mutate(lastInput.current);
  }, [mutation]);

  const reset = useCallback(() => {
    setState("idle");
    setError(null);
    setProgress(0);
    lastInput.current = null;
  }, []);

  return {
    submit,
    retry,
    reset,
    state,
    progress,
    error,
    isBusy:
      state === "uploading" || state === "finalizing" || state === "processing",
  };
}
